/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useEffect, useState } from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import {
  Avatar,
  Badge,
  Box,
  Card,
  CardContent,
  IconButton,
  TextField,
  Typography,
  Link as MuiLink,
  useTheme,
  useMediaQuery,
  DialogTitle,
  Alert,
  AlertTitle,
  Divider,
  CircularProgress,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { Camera, LogOut, Save } from 'lucide-mui';
import { QRCodeSVG } from 'qrcode.react';
import { Link, useNavigate } from 'react-router-dom';
import { RootState } from '../../../reducers';
import { updateUserProfile } from '../settingsSlice';
import ImageCropTool from './ImageCropTool';
import { setAlertMessage } from '../../dashboard/dashboardSlice';
import { getSignoutUrl } from '../../../lib/routingUtils';
import { getToken } from '../../../lib/localStorageHandler';
import { scheduleDeleteAccount } from '../../auth/authThunks';
import { getInitialState } from '../../app/appSlice';
import { setDeleteAccountDialogOpen } from '../../auth/authSlice';

const ProfileSettings = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const token = getToken();
  const { user, status } = useSelector((state: RootState) => ({
    user: state?.settings.user,
    status: state?.settings.updateSettings.status,
  }));
  const { name, email, picture } = user || {};
  const { teams } = useSelector((state: RootState) => state?.team);
  const { isDialogOpen: isDeleteAccountDialogOpen } = useSelector(
    (state: RootState) => state?.auth.deleteAccount
  );
  const [newName, setNewName] = useState<string>(name);
  const [isPictureDialogOpen, setPictureDialogOpen] = useState(false);
  const [croppingProfilePicture, setCroppingProfilePicture] = useState<
    string | null
  >(null);
  const [newProfilePicture, setNewProfilePicture] = useState<string | null>(
    null
  );
  const [confirmationText, setConfirmationText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const theme = useTheme();
  const isMediumOrBelowViewport = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    setNewName(name);
  }, [name]);

  useEffect(() => {
    if (status === 'succeeded') {
      dispatch(
        setAlertMessage({
          type: 'success',
          message: 'Profile updated successfully',
        })
      );
    } else if (status === 'failed') {
      setAlertMessage({ type: 'error', message: 'Failed to update profile' });
    }
  }, [status]);

  const updateButtonOnClick = () => {
    if (!newName) {
      setAlertMessage({ type: 'error', message: 'Name is required' });
      return;
    }

    const dataURLMatches = newProfilePicture
      ? /data:(.*);base64,(.+)/.exec(newProfilePicture)
      : null;
    const pictureData =
      dataURLMatches?.length === 3
        ? {
            pictureMime: dataURLMatches[1],
            picture: dataURLMatches[2],
          }
        : {};

    dispatch(updateUserProfile({ name: newName, ...pictureData }));
  };

  const nameOnChange = (value?: string) => {
    if (value) {
      setNewName(value);
    }
  };

  const setDeleteDialogOpen = (open: boolean) => {
    dispatch(setDeleteAccountDialogOpen(open));
    if (!open) {
      setConfirmationText('');
    }
  };

  const handleDeleteAccount = async () => {
    if (confirmationText === 'DELETE') {
      setIsDeleting(true);
      await dispatch(scheduleDeleteAccount());
      dispatch(
        setAlertMessage({
          type: 'success',
          message:
            'Account deletion initiated. You will be logged out in 5 seconds.',
        })
      );

      setTimeout(() => {
        navigate(getSignoutUrl());
      }, 5000);

      setDeleteDialogOpen(false);
      setIsDeleting(false);
    }
  };

  const liveTeams = teams.filter((team) => !team.deleteRequestedAt).length;

  return (
    <Box>
      <Card sx={{ maxWidth: '500px', margin: '0 auto' }}>
        <CardContent>
          <Box
            sx={{
              textAlign: 'center',
            }}
          >
            <Badge
              overlap="circular"
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              badgeContent={
                <IconButton
                  size="large"
                  title="edit"
                  onClick={() => setPictureDialogOpen(true)}
                  sx={{
                    '&:hover': {
                      backgroundColor: 'white',
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: 24,
                      height: 24,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Box
                      sx={{
                        transform: 'scale(1.2)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Camera />
                    </Box>
                  </Box>
                </IconButton>
              }
            >
              <Avatar
                alt="Profile Image"
                src={newProfilePicture || picture}
                style={{ width: 100, height: 100 }}
              />
            </Badge>
            <Typography variant="h6" sx={{ mb: 2 }}>
              {name}
            </Typography>
            <TextField
              variant="outlined"
              id="profileName"
              label="Name"
              value={newName}
              error={!newName}
              onChange={(event) => nameOnChange(event.target.value)}
              required
              fullWidth
              sx={{ mb: 2 }}
            />
            <TextField
              variant="outlined"
              id="profileEmail"
              label="Email"
              value={email}
              disabled
              fullWidth
              sx={{ mb: 2 }}
            />
            <Box
              sx={{
                display: 'flex',
                flexDirection: isMediumOrBelowViewport ? 'column' : 'row',
                justifyContent: 'space-between',
              }}
            >
              <Button
                variant="contained"
                onClick={updateButtonOnClick}
                fullWidth
                sx={{
                  flex: 1,
                  mr: isMediumOrBelowViewport ? 0 : 1,
                  mb: isMediumOrBelowViewport ? 1 : 0,
                  height: '42px',
                }}
                startIcon={
                  <Box sx={{ fontSize: 20 }}>
                    <Save />
                  </Box>
                }
              >
                Update Profile
              </Button>
              <Link
                to={getSignoutUrl()}
                style={{
                  flex: 1,
                  marginLeft: isMediumOrBelowViewport ? 0 : '8px',
                }}
              >
                <Button
                  data-testid="signout-button"
                  variant="outlined"
                  color="secondary"
                  fullWidth
                  href=""
                  sx={{ height: '42px' }}
                  startIcon={
                    <Box sx={{ fontSize: 20 }}>
                      <LogOut />
                    </Box>
                  }
                >
                  Logout
                </Button>
              </Link>
            </Box>
            <Box
              sx={{
                display: 'none',
                flexDirection: 'column',
                alignItems: 'center',
                padding: theme.spacing(2),
              }}
            >
              {token && <QRCodeSVG value={token} />}
              <Typography variant="caption" sx={{ mt: 1 }}>
                Scan to Login to ScreenApp mobile app
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>
      <Box sx={{ textAlign: 'center', mt: 2 }}>
        <Button
          variant="text"
          color="error"
          onClick={() => setDeleteDialogOpen(true)}
        >
          Delete Account
        </Button>
      </Box>
      <Dialog
        open={isDeleteAccountDialogOpen && liveTeams <= 1}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h6">Delete Account</Typography>
        </DialogTitle>
        <Divider />
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 3 }}>
            This action cannot be undone
          </Alert>
          <Typography variant="body1" sx={{ mb: 3 }}>
            This will:
            <ul>
              <li>Delete all your recordings and transcripts</li>
              <li>Remove all your team memberships</li>
              <li>Cancel any active subscriptions</li>
              <li>
                Your account and all associated data will be permanently deleted
                after 14 days
              </li>
            </ul>
          </Typography>
          <TextField
            variant="outlined"
            label="Type DELETE to confirm"
            value={confirmationText}
            onChange={(e) => setConfirmationText(e.target.value)}
            fullWidth
            error={confirmationText !== '' && confirmationText !== 'DELETE'}
            helperText={
              confirmationText !== '' && confirmationText !== 'DELETE'
                ? 'Please type DELETE exactly as shown'
                : ''
            }
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            variant="outlined"
            color="secondary"
            onClick={() => setDeleteDialogOpen(false)}
            disabled={isDeleting}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteAccount}
            disabled={confirmationText !== 'DELETE' || isDeleting}
            startIcon={
              isDeleting ? <CircularProgress size={20} color="inherit" /> : null
            }
          >
            {isDeleting ? 'Deleting...' : 'Delete Account'}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={isDeleteAccountDialogOpen && liveTeams > 1}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="h6">Unable to Delete Account</Typography>
          </Box>
        </DialogTitle>
        <Divider />
        <DialogContent>
          <Alert
            severity="warning"
            sx={{
              mb: 3,
              '& .MuiAlert-icon': {
                transform: 'scale(1.2)',
              },
            }}
          >
            <AlertTitle sx={{ fontWeight: 600 }}>Action Required</AlertTitle>
            You need to delete or transfer ownership of your teams first
          </Alert>
          <Typography sx={{ mt: 2 }}>
            You are the primary owner of{' '}
            {teams.reduce((prev, team, index) => {
              const teamLinkNew = (
                <MuiLink
                  key={team.teamId}
                  onClick={() =>
                    window.open(
                      `${
                        window.location.href.split('#')[0]
                      }#/settings/team?teamId=${team.teamId}`,
                      '_blank'
                    )
                  }
                  sx={{ cursor: 'pointer', fontWeight: 'medium' }}
                >
                  {team.name}
                </MuiLink>
              );
              return index === 0 ? [teamLinkNew] : [prev, ', ', teamLinkNew];
            }, [])}
            . Please delete all except one team before proceeding with account
            deletion.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            variant="outlined"
            color="secondary"
            onClick={() => setDeleteDialogOpen(false)}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={async () => {
              setDeleteDialogOpen(false);
              await dispatch(getInitialState());
              setDeleteDialogOpen(true);
            }}
          >
            Retry
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={isPictureDialogOpen}
        maxWidth="md"
        fullWidth
        onClose={() => {
          setPictureDialogOpen(false);
          setCroppingProfilePicture(null);
        }}
      >
        <DialogTitle>
          <Typography variant="h6">Edit profile photo</Typography>
        </DialogTitle>
        <DialogContent>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 2,
              py: 2,
            }}
          >
            {!croppingProfilePicture && (
              <Typography color="text.secondary" variant="body2">
                Choose a new profile photo or drag and drop one here
              </Typography>
            )}
            <ImageCropTool
              onCrop={(image: string) => {
                setCroppingProfilePicture(image);
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            variant="text"
            color="inherit"
            onClick={() => {
              setPictureDialogOpen(false);
              setCroppingProfilePicture(null);
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={!croppingProfilePicture}
            onClick={() => {
              setPictureDialogOpen(false);
              setNewProfilePicture(croppingProfilePicture);
              setCroppingProfilePicture(null);
            }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProfileSettings;
